import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/** The type-scale steps from index.css — so `text-body text-graphite` keeps both (size + colour). */
const twMerge = extendTailwindMerge({
  extend: { theme: { text: ['caption', 'ui', 'body', 'lead', 'title-sm', 'title', 'h2'] } },
});

/** Joins class names and lets later Tailwind utilities override earlier ones (e.g. w-auto over w-full). */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatNumber(n: number) {
  return new Intl.NumberFormat('en-US').format(n);
}

export function formatDate(
  iso: string | null | undefined,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', opts).format(new Date(iso));
}

export function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d} d ago`;
  return formatDate(iso);
}

/** Renders text with paragraph breaks from plain CMS text. */
export const paragraphs = (text: string | null | undefined) =>
  (text ?? '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

export const isExternal = (href: string) => /^https?:\/\//i.test(href);

/** WhatsApp click-to-chat link for an international number such as "+213 781 60 68 17". */
export const whatsappHref = (number: string) => `https://wa.me/${number.replace(/\D/g, '')}`;
